'use client';

import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@coas/ui/dialog';
import { cn } from '@coas/ui/lib';
import { Provider, useAtom, useAtomValue } from 'jotai';

import { handlerArgsAtom, handlerAtom, messageAtom, openAtom } from '../model/state';
import { confirmationStore } from '../model/store';

type ConfirmationProviderProps = object;

const ConfirmationProvider = () => {
    const [open, setOpen] = useAtom(openAtom);
    const message = useAtomValue(messageAtom);
    const handler = useAtomValue(handlerAtom);
    const handlerArgs = useAtomValue(handlerArgsAtom);

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogContent
                className={cn(
                    'z-[100] w-[min(92vw,580px)] rounded-12 border border-neutral-200 bg-neutral-50 text-neutral-950 shadow-lg outline-none',
                    'max-h-[min(80vh,calc(var(--visual-viewport-height)-2rem))] overflow-hidden',
                    'p-0',
                    '[&>button]:right-4 [&>button]:top-4 md:[&>button]:right-6 md:[&>button]:top-6',
                    '[&>button]:z-10',
                )}
            >
                <div className='flex max-h-full flex-col gap-4 px-4 py-4 md:px-6 md:py-6'>
                    <DialogHeader className='text-left pr-10'>
                        <div className='flex flex-col gap-2'>
                            <DialogTitle className='text-18 font-600 text-neutral-950 md:text-22'>
                                Массовое управление категориями
                            </DialogTitle>

                            <DialogDescription className='text-left text-18 leading-snug text-neutral-500'>
                                {message}
                            </DialogDescription>
                        </div>
                    </DialogHeader>

                    <DialogFooter className='p-0'>
                        <div className='flex flex-row items-center justify-end gap-2 sm:gap-3'>
                            <DialogClose asChild>
                                <button
                                    type='button'
                                    onClick={() => setOpen(false)}
                                    className={cn(
                                        'h-9 px-3 text-13 md:px-4 md:text-14',
                                        'rounded-8 font-500 shrink-0',
                                        'bg-neutral-200 text-neutral-900',
                                        'transition-colors duration-150 hover:bg-neutral-300',
                                        'disabled:pointer-events-none disabled:opacity-60',
                                    )}
                                >
                                    Отмена
                                </button>
                            </DialogClose>

                            <button
                                type='button'
                                className={cn(
                                    'h-9 px-3 text-13 md:px-4 md:text-14',
                                    'rounded-8 font-700 shrink-0',
                                    'bg-red-500 text-white',
                                    'transition-opacity duration-150',
                                    'disabled:pointer-events-none disabled:opacity-60',
                                )}
                                onClick={() => {
                                    try {
                                        handler?.(...handlerArgs);
                                    } finally {
                                        setOpen(false);
                                    }
                                }}
                            >
                                Подтвердить
                            </button>
                        </div>
                    </DialogFooter>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default ({ children, ...props }: ConfirmationProviderProps & React.PropsWithChildren) => {
    return (
        <>
            <Provider store={confirmationStore}>
                <ConfirmationProvider {...props} />
            </Provider>

            {children}
        </>
    );
};
